import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ReferralDrawer } from '../../components/referral-drawer/ReferralDrawer';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Loader2, Download } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  fetchReferralFilterOptions,
  fetchReferrals,
} from '../../services/referralService';
import { useWorkflowBar } from '../../contexts/WorkflowBarContext';
import { ReferralTable, DEFAULT_SORT, REFERRAL_COLUMNS, getReferralSelectionId, getVisibleReferralColumns } from '../../components/referrals/ReferralTable';
import { useReferralList } from '../../hooks/useReferralList';
import { getCurrentUser, getOrgType, isSmartLightEmployee } from '../../authUtils';


function ReferralListPage() {
  const {
    setCounts, setOverdueCounts, setTotalReferrals, setVisible,
    workflowStepFilter, assignedToFilter,
    stepLabels, setStepLabels,
  } = useWorkflowBar();

  const { searchText = '' } = useOutletContext() || {};

  const {
    referrals,
    totalCount,
    loading,
    error,
    sortColumns,
    setSortColumns,
    isDefaultSort,
    setIsDefaultSort,
    openSortMenu,
    setOpenSortMenu,
    filters,
    setFilters,
    filterOptions,
    handleSort,
    handleClearColumnSort,
    patchReferral,
    workflowCounts,
  } = useReferralList(fetchReferrals, searchText, {
    fetchOptionsFn: fetchReferralFilterOptions,
    workflowStepFilter,
    assignedToFilter,
  });

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [filteredCount, setFilteredCount] = useState(0);

  const handleRowDoubleClick = (referral) => {
    setSelectedReferral(referral);
    setDrawerOpen(true);
  };

  const handleFilteredCountChange = useCallback((count) => {
    setFilteredCount(count);
  }, []);
  const visibleColumns = getVisibleReferralColumns(REFERRAL_COLUMNS, {
    isInternal: isSmartLightEmployee(),
    orgType: getOrgType(),
    displayRules: getCurrentUser()?.display_rules,
  });

  // Drive the workflow bar from the server-computed counts (full filtered
  // dataset, not just the visible page). The counts response carries the
  // step labels too, so it's the single source for both labels and totals.
  useEffect(() => {
    if (!workflowCounts?.steps) return;
    const { steps, total_count: total = 0 } = workflowCounts;
    setStepLabels(steps.map((s) => ({ key: s.key, label: s.label })));
    setCounts(Object.fromEntries(steps.map((s) => [s.key, s.count ?? 0])));
    setOverdueCounts(Object.fromEntries(steps.map((s) => [s.key, s.overdue_count ?? 0])));
    setTotalReferrals(total);
    setVisible(total > 0);
  }, [workflowCounts, setStepLabels, setCounts, setOverdueCounts, setTotalReferrals, setVisible]);

  // Hide the bar when leaving this page
  useEffect(() => {
    return () => setVisible(false);
  }, [setVisible]);

  useEffect(() => {
    if (loading || selectedRowId === null) return;
    const selectedReferralAvailable = referrals.some((referral) => getReferralSelectionId(referral) === selectedRowId);
    if (!selectedReferralAvailable) {
      setSelectedRowId(null);
    }
  }, [loading, referrals, selectedRowId]);

  return (
    <div className="bg-background font-sans antialiased -m-6 h-[calc(100vh-88px)] flex flex-col">
      {/* Referral detail drawer */}
      <ReferralDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onActivityPosted={patchReferral}
        referral={selectedReferral}
        // SmartLight users get a read-only Carrier Portal — comment/status
        // creation happens only in the Carrier Engagement Dashboard, and a
        // read-only drawer keeps the portal screen-share-safe. External
        // (carrier/TPA/client) users keep the composer.
        readOnly={isSmartLightEmployee()}
      />
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-col flex-1 min-h-0">
        <div className="flex justify-end mb-0">
          <Button
            variant="ghost"
            size="sm"
            data-testid="default-sort-button"
            className={cn(
              'text-xs gap-1',
              isDefaultSort
                ? 'text-primary hover:bg-transparent hover:text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            )}
            onClick={() => {
              if (isDefaultSort) {
                setSortColumns([]);
                setIsDefaultSort(false);
              } else {
                setSortColumns([]);
                setIsDefaultSort(true);
              }
            }}
          >
            <svg className="!h-3 !w-3" viewBox="5 5 14 14" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" strokeLinecap="round">
              <path d="M8.5 11l2.7-4.6a1 1 0 0 1 1.6 0L15.5 11" />
              <path d="M15.5 13l-2.7 4.6a1 1 0 0 1-1.6 0L8.5 13" />
            </svg>
            Default Table Sort
          </Button>
        </div>
        <Card className="flex flex-col flex-1 min-h-0">
          <CardContent className="p-0 flex flex-col flex-1 min-h-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-destructive">Error: {error}</p>
              </div>
            ) : (
              <div className="overflow-x-auto flex-1 min-h-0" data-testid="referral-list-table">
                <ReferralTable
                  columns={visibleColumns}
                  rows={referrals}
                  emptyStateText={searchText ? 'No results found for this search.' : 'No referrals found.'}
                  sortColumns={sortColumns}
                  setSortColumns={setSortColumns}
                  isDefaultSort={isDefaultSort}
                  setIsDefaultSort={setIsDefaultSort}
                  defaultSort={DEFAULT_SORT}
                  openSortMenu={openSortMenu}
                  setOpenSortMenu={setOpenSortMenu}
                  onSort={handleSort}
                  onClearColumnSort={handleClearColumnSort}
                  filters={filters}
                  filterOptions={filterOptions}
                  onFilterChange={(filterKey, val) => setFilters(prev => ({ ...prev, [filterKey]: val }))}
                  onRowDoubleClick={handleRowDoubleClick}
                  selectedRowId={selectedRowId}
                  onSelectedRowChange={setSelectedRowId}
                  stepLabels={stepLabels}
                  onFilteredCountChange={handleFilteredCountChange}
                />
              </div>
            )}
            {!loading && !error && referrals.length > 0 && (
              <div className="sticky bottom-0 z-10 flex items-center justify-center gap-4 border-t border-border bg-accent px-3 py-1">
                <span className="text-xs text-muted-foreground">
                  {filteredCount} of {totalCount} results
                </span>
                {/* TODO: wire up download functionality */}
                <button
                  type="button"
                  className="h-6 px-4 text-xs font-semibold flex items-center gap-1.5 text-muted-foreground hover:text-foreground bg-background hover:bg-accent border border-muted-foreground/30 hover:border-foreground rounded transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Download
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export { ReferralListPage };
